import { ScreenHeader } from '@/components/layout/screen-header';
import { Button } from '@/components/ui/button';
import { Text } from '@/components/ui/text';
import { TextInput } from '@/components/ui/text-input';
import { useAuth } from '@/hooks/use-auth';
import { router } from 'expo-router';
import React, { useEffect, useState } from 'react';
import { ScrollView, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

const CONFIRMATION_PHRASE = 'DELETE';
const GENERIC_ERROR = 'We could not delete your account. Please try again.';
const FORGOT_PASSWORD_LABEL = 'Forgot your password?';
/**
 * In-app Sign-in & Security password screen. A user who registered with a
 * password and later signed in with Google may not remember it; without this
 * they would have to sign out to recover, which they cannot do mid-deletion.
 */
const PASSWORD_ROUTE = '/(root)/password';

export function DeleteAccountScreen() {
  const { deleteAccount, checkHasPassword } = useAuth();

  const [confirmation, setConfirmation] = useState('');
  const [password, setPassword] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // null while we resolve whether this account has a password. Social-only
  // users (no credential account) are not asked for one.
  const [requiresPassword, setRequiresPassword] = useState<boolean | null>(null);

  useEffect(() => {
    let cancelled = false;
    checkHasPassword()
      .then((hasPassword) => {
        if (!cancelled) setRequiresPassword(hasPassword);
      })
      .catch(() => {
        // Fail safe: assume a password is required so we never skip re-auth.
        if (!cancelled) setRequiresPassword(true);
      });
    return () => {
      cancelled = true;
    };
  }, [checkHasPassword]);

  const isConfirmed = confirmation === CONFIRMATION_PHRASE;
  const passwordSatisfied = requiresPassword === false || password.length > 0;
  const canSubmit =
    isConfirmed && passwordSatisfied && requiresPassword !== null && !isDeleting;

  const handleDelete = async () => {
    if (!canSubmit) return;

    setIsDeleting(true);
    setError(null);

    try {
      await deleteAccount(requiresPassword ? password : undefined);
      // Success: the session is now gone. The root layout watches auth status
      // and redirects to the auth stack automatically -- no manual navigation.
    } catch (e) {
      const message = e instanceof Error && e.message ? e.message : GENERIC_ERROR;
      setError(message);
      setIsDeleting(false);
    }
  };

  return (
    <SafeAreaView className="flex-1">
      <ScreenHeader title="Delete Account" showBackButton />
      <ScrollView
        className="flex-1"
        showsVerticalScrollIndicator={false}
        contentContainerClassName="px-4 pb-8"
      >
        <Text className="text-2xl font-bold mt-2 mb-4 text-destructive">
          Delete your account
        </Text>

        <Text className="text-base mb-4 text-foreground">
          This will permanently delete your account. This action cannot be
          undone.
        </Text>

        <View className="rounded-xl p-4 mb-6 bg-card">
          <Text className="text-sm leading-5 text-muted-foreground">
            Your private data (profile, saved lists, and uploaded photos) will be
            permanently removed. Your public reviews and check-ins will remain
            visible to others, but will no longer be linked to you -- they will
            appear as posted by &quot;Flavrly User&quot;.
          </Text>
        </View>

        <TextInput
          label={`Type ${CONFIRMATION_PHRASE} to confirm`}
          placeholder={CONFIRMATION_PHRASE}
          value={confirmation}
          onChangeText={(text) => {
            setConfirmation(text);
            if (error) setError(null);
          }}
          autoCapitalize="characters"
          autoCorrect={false}
          containerClassName="mb-4"
        />

        {requiresPassword === true && (
          <>
            <TextInput
              label="Password"
              placeholder="Enter your password"
              value={password}
              onChangeText={(text) => {
                setPassword(text);
                if (error) setError(null);
              }}
              secureTextEntry
              autoCapitalize="none"
              containerClassName="mb-1"
            />

            <View className="flex-row justify-end mb-1">
              <Button
                variant="link"
                size="sm"
                onPress={() => router.push(PASSWORD_ROUTE)}
                accessibilityLabel={FORGOT_PASSWORD_LABEL}
                testID="delete-account-forgot-password"
              >
                {FORGOT_PASSWORD_LABEL}
              </Button>
            </View>
          </>
        )}

        {error && (
          <Text
            className="text-sm mt-1 mb-2 text-destructive"
            accessibilityLiveRegion="polite"
          >
            {error}
          </Text>
        )}

        <Button
          variant="destructive"
          onPress={handleDelete}
          disabled={!canSubmit}
          loading={isDeleting}
          accessibilityLabel="Delete my account permanently"
          className="mt-4"
        >
          Delete My Account
        </Button>
      </ScrollView>
    </SafeAreaView>
  );
}
